import { Alert, Chip, Tooltip } from "@mui/joy";
import { Box } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useGetOrganizationsQuery } from "@nerdjs/account-kit";
import { useDispatch } from "react-redux";
import {
  NOTICE_PERIOD_DAYS,
  daysUntilEffective,
  givesFullNotice,
  tosKindLabel,
} from "../../entities/tosVersion";
import { setFormOpen, setSelectedTosVersionID } from "../../reducers/rhapsody";
import { useTosVersions } from "../../redux/tosVersion/tosVersionHooks";
import Id from "../id/id";

/**
 * Published legal documents — Terms, Privacy Policy, Payroll Addendum.
 *
 * Rows open the edit dialog rather than editing in place. A published version is
 * something organizations have acknowledged, and the fields interact — a scoped
 * document needs an organization list, a privacy notice has nothing to accept —
 * so it wants a form that can show those relationships, not seven independent
 * cells with no idea about each other.
 *
 * No delete, here or in rhapsody-service: removing a row would orphan the
 * acknowledgements that reference it and destroy the evidence organizations
 * agreed. Retire a version by switching Active off.
 *
 * The notice-period check warns rather than blocks. Our Terms promise customers
 * thirty days before a change binds them, and publishing inside that window
 * breaks the promise — but a genuinely urgent legal or security change may have
 * to go out sooner, so it is surfaced loudly and left to a human.
 *
 * @returns {null} TosVersions
 */
export default function TosVersions() {
  const { tosVersions } = useTosVersions();
  const { data: orgData } = useGetOrganizationsQuery();
  const dispatch = useDispatch();

  const rows = tosVersions ?? [];

  const orgName = (id: number) => orgData?.entities[id]?.name ?? `#${id}`;

  const shortNotice = rows.filter(
    (v) =>
      v.active &&
      v.effectiveAt &&
      !givesFullNotice(v.effectiveAt) &&
      (daysUntilEffective(v.effectiveAt) ?? -1) >= 0
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {shortNotice.length > 0 ? (
        <Alert
          color="warning"
          startDecorator={<i className="fa-solid fa-triangle-exclamation"></i>}
        >
          {shortNotice.length === 1 ? "A version takes" : "Versions take"}{" "}
          effect in less than {NOTICE_PERIOD_DAYS} days. Our Terms promise
          customers {NOTICE_PERIOD_DAYS} days&apos; notice before a change binds
          them — publishing sooner breaks that commitment unless the change is
          urgent.
        </Alert>
      ) : null}
      <DataGridPremium
        rows={rows}
        density="compact"
        autoHeight
        onRowClick={(p) => {
          dispatch(setSelectedTosVersionID(p.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "tosVersion" }));
        }}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        columns={[
          {
            field: "id",
            headerName: "ID",
            type: "number",
            width: 70,
            renderCell: (p) => {
              if (p.id === -1) return <Box />;
              return (
                <Id
                  startDecorator={<i className="fa-solid fa-file-contract"></i>}
                >
                  {p.id}
                </Id>
              );
            },
          },
          {
            field: "kind",
            headerName: "Document",
            width: 200,
            valueGetter: (p) => tosKindLabel(p.value),
          },
          { field: "version", headerName: "Version", width: 120 },
          {
            field: "effectiveAt",
            headerName: "Effective",
            width: 180,
            renderCell: (p) => {
              const days = daysUntilEffective(p.value);
              if (days === undefined) return <Box>—</Box>;
              const short = days >= 0 && days < NOTICE_PERIOD_DAYS;
              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {String(p.value).slice(0, 10)}
                  {days >= 0 ? (
                    <Chip
                      size="sm"
                      color={short ? "warning" : "neutral"}
                      variant="soft"
                    >
                      in {days}d
                    </Chip>
                  ) : (
                    <Chip size="sm" color="success" variant="soft">
                      in force
                    </Chip>
                  )}
                </Box>
              );
            },
          },
          {
            field: "requiresAcceptance",
            headerName: "Acceptance",
            width: 140,
            renderCell: (p) =>
              p.value === false ? (
                <Tooltip title="Shown to organizations, but there is nothing to accept">
                  <Chip size="sm" variant="soft" color="neutral">
                    notice only
                  </Chip>
                </Tooltip>
              ) : (
                <Chip size="sm" variant="soft" color="primary">
                  required
                </Chip>
              ),
          },
          {
            field: "mode",
            headerName: "Mode",
            width: 120,
            renderCell: (p) => (
              <Tooltip
                title={
                  p.value === "blocking"
                    ? "Organizations must acknowledge before they can continue"
                    : "Organizations see a dismissible notice"
                }
              >
                <Chip
                  size="sm"
                  variant="soft"
                  color={p.value === "blocking" ? "danger" : "primary"}
                >
                  {p.value ?? "soft"}
                </Chip>
              </Tooltip>
            ),
          },
          {
            field: "audience",
            headerName: "Applies to",
            width: 240,
            renderCell: (p) => {
              if (p.value !== "organizations") {
                return <Box>All organizations</Box>;
              }
              const ids: number[] = p.row.organizationIDs ?? [];
              if (ids.length === 0) {
                return (
                  <Tooltip title="Scoped to specific organizations, but none are selected — nobody will see it">
                    <Chip size="sm" variant="soft" color="warning">
                      no organizations
                    </Chip>
                  </Tooltip>
                );
              }
              return (
                <Tooltip title={ids.map(orgName).join(", ")}>
                  <Box>
                    {ids.length === 1
                      ? orgName(ids[0])
                      : `${ids.length} organizations`}
                  </Box>
                </Tooltip>
              );
            },
          },
          {
            field: "active",
            headerName: "Active",
            width: 90,
            type: "boolean",
          },
          { field: "summary", headerName: "Summary", flex: 1 },
        ]}
      />
    </Box>
  );
}
